import cn from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';

import assignTextStyleToSelectionOperation from 'editor/src/store/design/operation/assignTextStyleToSelectionOperation';
import updateTextStyleFromSelectionOperation from 'editor/src/store/design/operation/updateTextStyleFromSelectionOperation';
import { TextStyleUpdateResult } from 'editor/src/store/design/operation/updateTextStyleOperation';
import getSelectionTextStyle from 'editor/src/store/design/selector/getSelectionTextStyle';
import { getTextStyleMembers, isTextStyleProtected } from 'editor/src/store/design/util/textStyles';
import { useDispatch, useSelector } from 'editor/src/store/hooks';
import { isEmbedded } from 'editor/src/store/watchers/useDisplayMode';

import toastController from 'editor/src/util/toastController';

import WithTooltip from 'editor/src/component/WithTooltip';

import { FONT_FAILED_TOAST_KEY } from './constants';

import styles from './TextStyleModifiedBar.module.scss';

interface Props {
  className?: string;
  /** In the mobile text toolbar. */
  mobile?: boolean;
}

/**
 * GAPI-61895: "format one, update all". When the selected text follows a style but no longer
 * looks like it, the toolbar shows `Heading · Modified [Update all 4 texts] [Reset]` as its own
 * item: Update makes the style (and every text following it) take this text's look,
 * Reset puts this text back to the style. Each is one undo step. Renders nothing otherwise.
 */
function TextStyleModifiedBar({ className, mobile }: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const selection = useSelector(getSelectionTextStyle, shallowEqual);
  const styleId = selection.kind === 'style' ? selection.style.id : undefined;
  // The texts the update rewrites: the style's texts the user may edit.
  const count = useSelector((state) =>
    styleId
      ? getTextStyleMembers(state.design.designData, styleId).filter(
          ({ element }) => !isTextStyleProtected(state, element),
        ).length
      : 0,
  );

  if (selection.kind !== 'style' || !selection.modified) {
    return null;
  }
  const { style } = selection;

  const warnIfFailed = (result: TextStyleUpdateResult) => {
    if (result === 'failed') {
      toastController.warning(t(FONT_FAILED_TOAST_KEY), '');
    }
  };

  return (
    <div
      className={cn(styles.TextStyleModifiedBar, className, { [styles.mobile]: mobile }, 'cy-text-style-modified-bar')}
    >
      <span className={styles.status}>
        <span className={styles.name}>{style.name}</span>
        {` · ${t('editor-text-style-modified')}`}
      </span>
      <WithTooltip overlay={t('editor-text-style-update-all-tooltip', { count, name: style.name })}>
        <button
          type="button"
          className={cn(styles.button, styles.primary, 'cy-text-style-update')}
          onClick={async () => warnIfFailed(await dispatch(updateTextStyleFromSelectionOperation(style.id)))}
        >
          {count === 1 ? t('editor-text-style-update-all-one') : t('editor-text-style-update-all', { count })}
        </button>
      </WithTooltip>
      <button
        type="button"
        className={cn(styles.button, 'cy-text-style-reset')}
        onClick={async () => warnIfFailed(await dispatch(assignTextStyleToSelectionOperation(style.id)))}
      >
        {isEmbedded ? t('editor-text-style-restore') : t('editor-reset')}
      </button>
    </div>
  );
}

export default React.memo(TextStyleModifiedBar);
